import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { List, RefreshCw, BookOpen, Sparkles, Loader2 } from 'lucide-react';
import {
  TopPerformersCard, AlertsCard, DrillCard, RegionCompetencyLineChart,
} from '../../../components/LevelDashboard';
import {
  useGetNationalHolisticSummaryQuery,
  TermFilter,
  termFilterToQueryValue,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';
import { useGetKvsResultAllClassSummaryQuery } from '../../../services/kvsResultApi';
import { StageScoreBandCard } from '../../../components/ScoreBandPanel';
import { FoundationKPIHero } from '../components/FoundationKPIHero';
import { FoundationMasterTabs } from '../components/FoundationMasterTabs';
import { NationalOverview } from '../../../types';
import KVSLoader from '../../../../../components/loaders/KVSLoader';

const BASE = '/kvs-samagam/student-analytics';

const emptyOverview: NationalOverview = {
  metrics: {
    totalStudents: 0,
    avgAttendance: 0,
    avgCompetency: 0,
    needsSupportCount: 0,
    excellentCount: 0,
    avgBehaviorScore: 0,
    avgPeerScore: 0,
    bmiNormalPercent: 0,
  },
  competencyBuckets: [
    { level: 'Excellent', count: 0, percent: 0 },
    { level: 'Good', count: 0, percent: 0 },
    { level: 'Average', count: 0, percent: 0 },
    { level: 'Below Average', count: 0, percent: 0 },
  ],
  attendanceTrend: [],
  regions: [],
  topPerformers: [],
  alerts: [],
};

const HolisticDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return (yearsRes?.data || [2026, 2025]).map(Number);
  }, [yearsRes]);

  const [academicYear, setAcademicYear] = useState<number>(2025);

  React.useEffect(() => {
    if (yearsRes?.data?.length) {
      const numericYears = yearsRes.data.map(Number);
      if (!numericYears.includes(Number(academicYear))) {
        setAcademicYear(numericYears[0]);
      }
    }
  }, [yearsRes, academicYear]);

  const [term, setTerm] = useState<TermFilter>('overall');

  // Every number on this page, aggregated by the server — one small request for
  // any term, Overall included. It used to download every student record
  // (two ~125 MB payloads for Overall) and compute these in the browser.
  const {
    data: summary,
    currentData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetNationalHolisticSummaryQuery({ academicYear, term });
  const hasError = Boolean(error);

  const overview = summary?.success ? summary.overview : emptyOverview;
  const { metrics, regions, topPerformers, alerts } = overview;
  const regionCompetencyTrend = summary?.success ? summary.regionCompetencyTrend : [];
  const querySuffix = `?year=${academicYear}&term=${termFilterToQueryValue(term)}`;

  // After a term/year change RTK Query keeps showing the previous selection's
  // numbers until the new ones arrive (isLoading stays false — it only covers the
  // very first load). Say so, and dim them, instead of silently showing stale data.
  const isShowingStale = isFetching && !currentData;

  const termLabel = term === 'overall' ? 'Overall' : `Term ${term}`;

  // The Foundational card on Stage Selection, fetched the same way, so this page shows
  // its students-by-score-band split with identical figures. The request's year is the
  // results year (HPC year + 1). Term is left to the backend default (Exit, Term 2)
  // unless Term 1 is chosen, which keeps the URL — and the cache — the stage page's.
  const { data: stageSummary } = useGetKvsResultAllClassSummaryQuery({
    year: academicYear + 1,
    ...(term === 1 ? { term: 1 } : {}),
  });
  const foundationBands = stageSummary?.data.stages.foundationStage;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[320px] gap-3">
        <KVSLoader />
        <p className="text-sm text-gray-500">Loading national analytics…</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {/* Stage switcher */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
          <BookOpen size={12} /> Foundational Stage &mdash; Balvatika I – Class II
        </span>
        <span className="text-gray-300 text-xs">|</span>
        <button
          onClick={() => navigate('/kvs-samagam/student-analytics/stages/preparatory')}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
        >
          <Sparkles size={12} /> Switch to Preparatory Stage
        </button>
      </div>

      {/* Header */}
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Student Holistic Development</h1>
          <p className="text-sm text-gray-500 mt-1">
            National Overview &mdash; Academic Year {academicYear}-{String(Number(academicYear) + 1).slice(-2)} &mdash; {termLabel} &mdash; {metrics.totalStudents.toLocaleString()} students across {regions.length} regions
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => {
                const value = e.target.value;
                setTerm(value === 'overall' ? 'overall' : value === '2' ? 2 : 1);
              }}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Session</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}-{String(Number(year) + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {hasError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            Unable to fetch analytics data for selected year/term. Please retry.
          </div>
        )}

        {isFetching && (
          <div
            role="status"
            className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-700"
          >
            <Loader2 size={14} className="animate-spin" />
            {isShowingStale
              ? `Loading ${termLabel} data for ${academicYear}-${String(Number(academicYear) + 1).slice(-2)}…`
              : 'Refreshing national analytics…'}
          </div>
        )}
      </div>

      <div
        className={`space-y-6 transition-opacity ${isShowingStale ? 'opacity-50 pointer-events-none' : ''}`}
        aria-busy={isFetching}
      >
      {/* Students by score band — the same figures as the Foundational card on Stage Selection. */}
      {foundationBands && foundationBands.totalStudents > 0 && (
        <StageScoreBandCard
          stats={foundationBands}
          stage="foundational"
          foundationTerm={term === 1 ? 1 : 2}
          matchesStageCard={term !== 1}
        >
          <p>
            {foundationBands.totalStudents.toLocaleString()} students with {term === 1 ? 'Entry (Term 1)' : 'Exit (Term 2)'} ratings,
            each placed by their own average score.
          </p>
          <p className="text-[12px] text-gray-500">
            The level tiles below count every rating separately, so their percentages differ.
          </p>
        </StageScoreBandCard>
      )}

      {/* KPI Hero — at national level it reads the competency rollup, not student records. */}
      <FoundationKPIHero
        records={[]}
        term={term}
        contextLabel={`${regions.length} regions · ${metrics.totalStudents.toLocaleString()} students`}
        year={academicYear}
        preferCounts
      />

      {/* Master Analytics Tabs — likewise rollup-driven at national level. */}
      <FoundationMasterTabs records={[]} hasData={metrics.totalStudents > 0} term={term} year={academicYear} />

      {regionCompetencyTrend.length > 0 && (
        <RegionCompetencyLineChart
          data={regionCompetencyTrend}
          onPointClick={(regionId) => navigate(`${BASE}/region/${regionId}${querySuffix}`)}
        />
      )}

      {/* Top / Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TopPerformersCard items={topPerformers} />
        <AlertsCard
          items={alerts}
          onViewAll={() => navigate(`${BASE}/list/regions/below-average${querySuffix}`)}
          viewAllLabel="Need Attention Regions"
        />
      </div>

      {/* Region Cards — Drill Down */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Regions — Drill Down</h2>
          <button
            onClick={() => navigate(`${BASE}/list/regions${querySuffix}`)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition"
          >
            <List size={16} /> See All Regions
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {regions.map(r => (
            <DrillCard
              key={r.regionId}
              title={r.regionName}
              subtitle={`State: ${r.stateCode}`}
              metrics={r.metrics}
              alerts={r.alerts}
              navigateTo={`region/${r.regionId}${querySuffix}`}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default HolisticDashboard;
